import axios, { AxiosRequestConfig } from 'axios';

import { bookingVerticalType } from '../constants';
import { DataResponse, JobType } from '../types';

const URL = '/v2/admin/qa-activity';

export type ActivityScope = 'JOB_ONLY' | 'JOB_INTEREST' | 'JOB_INTEREST_BOOKING' | 'FULL_FLOW';
export type PaymentType = 'CASH' | 'CREDIT' | 'CARE_CREDIT';
export type TimeOffset = 'PAST' | 'FUTURE' | 'TODAY';
export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
export type ActivityUserType = 'PARENT' | 'SITTER';

export interface ActivityRequest {
  parentId: number;
  sitterId?: number;
  activityScope: ActivityScope;
  targetBookingStatus?: string;
  jobType: JobType;
  bookingTypeId: number;
  paymentType: PaymentType;
  paymentFrequencyId?: number;
  startDate: string;
  startTime?: string;
  durationHours?: number;
  timeOffset?: TimeOffset;
  recurringDays?: DayOfWeek[];
  numberOfChildren?: number;
  childrenAges?: number[];
  hourlyRate?: number;
  autoApproveJob?: boolean;
  count?: number;
  spreadAcrossDays?: boolean;
  jobDescription?: string;
}

export interface GeneratedActivity {
  jobId?: number;
  bookingId?: number;
  interestedSitterUserId?: number;
  messageThreadId?: number;
  status: string;
  adminJobUrl?: string;
  adminBookingUrl?: string;
}

export interface ActivityResponse {
  success: boolean;
  message: string;
  activities: GeneratedActivity[];
  warnings: string[];
}

export interface EligibilityCheck {
  name: string;
  passed: boolean;
  message: string;
  canAutoFix: boolean;
  autoFixKey?: string;
  isWarning?: boolean;
}

export interface UserEligibilityDTO {
  userId: number;
  userType: ActivityUserType;
  displayName: string;
  email: string;
  checks: EligibilityCheck[];
  isEligible: boolean;
  canAutoFix: string[];
  isB2BUser: boolean;
  companyId?: number;
  companyName?: string;
  careCreditBalance?: number;
  careCreditByVertical?: Record<number, number>;
}

// Legacy alias — prefer `bookingVerticalType` from `us-web-services` directly.
// Kept for callers in apps/admin/qa-tools that still read CHILDCARE-style keys.
export const BOOKING_TYPES = {
  CHILDCARE: bookingVerticalType.CHILD_CARE,
  PETCARE: bookingVerticalType.PET_CARE,
  SENIORCARE: bookingVerticalType.SENIOR_CARE,
  HOUSEHOLD: bookingVerticalType.HOUSEHOLD_SERVICES,
} as const;

export const PAYMENT_FREQUENCIES = {
  HOURLY: 1,
  FLAT_RATE: 2,
} as const;

export interface AutoFixResult {
  success: boolean;
  message: string;
  requiresBypass: boolean;
}

export const TARGET_STATUSES = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  COMPLETED: 'closed_completed',
  CANCELLED: 'parent_cancel',
  EXPIRED: 'expired',
} as const;

class AdminQaActivityService {
  static generateActivity(
    data: ActivityRequest,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<ActivityResponse>> {
    return axios.post(URL, data, config);
  }

  static validateParent(
    parentId: number,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<UserEligibilityDTO>> {
    return axios.get(`${URL}/parent/${parentId}/validation`, config);
  }

  static autoFixParent(
    parentId: number,
    key: string,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<AutoFixResult>> {
    return axios.post(`${URL}/autofix/parent/${parentId}`, null, { ...config, params: { key } });
  }

  static autoFixSitter(
    sitterId: number,
    key: string,
    bookingTypeId?: number,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<AutoFixResult>> {
    const params = bookingTypeId ? { key, bookingTypeId } : { key };
    return axios.post(`${URL}/autofix/sitter/${sitterId}`, null, { ...config, params });
  }

  static validateSitter(
    sitterId: number,
    bookingTypeId?: number,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<UserEligibilityDTO>> {
    const params = bookingTypeId ? { bookingTypeId } : {};
    return axios.get(`${URL}/sitter/${sitterId}/validation`, { ...config, params });
  }
}

export { AdminQaActivityService };
